import React, { FC, ReactElement, useEffect } from 'react';

import { InternshipTalentMatchingCard } from '../../containers';
import { Candidate } from '../../models/Candidate';
import { CompetenciesFormData } from '../../models/Competencies';
import { SkillsFormData } from '../../models/Skills';
import { RoleType } from '../../models/User';
import { InternshipVacancyOverviewFilterValues } from '../../models/VacancyOverview';
import { useLegacySelector, useTypedDispatch, useTypedSelector } from '../../store';
import { patchCandidate } from '../../store/candidate/candidateActions';
import { fetchCompetencies } from '../../store/competencies/competenciesActions';
import { fetchEscoSkills } from '../../store/escoSkills/escoSkillsActions';
import { setActiveFilterValues } from '../../store/internshipVacancyOverview/internshipVacancyOverview';
import { fetchSkills } from '../../store/skills/skillsActions';

interface ConnectedInternshipTalentMatchingCardProps {
    isTalentMatching: boolean;
    className?: string;
}

const ConnectedInternshipTalentMatchingCard: FC<ConnectedInternshipTalentMatchingCardProps> = ({
    isTalentMatching,
    className = '',
}): ReactElement => {
    const dispatch = useTypedDispatch();

    const isAuthenticated = useLegacySelector(state => state.legacyUser.authenticated);
    const role = useLegacySelector(state => state.legacyUser.role);

    const candidate = useTypedSelector(state => state.candidateReducer.candidate);

    const isLoading = useTypedSelector(state => state.internshipVacancyOverviewReducer.isLoading);
    const activeFilterValues = useTypedSelector(state => state.internshipVacancyOverviewReducer.activeFilterValues);

    const skillOptions = useTypedSelector(state => state.skillsReducer.skills);
    const skillsError = useTypedSelector(state => state.skillsReducer.error);
    const escoSkillOptions = useTypedSelector(state => state.escoSkillsReducer.escoSkills);
    const escoSkillsError = useTypedSelector(state => state.escoSkillsReducer.error);
    const competencyOptions = useTypedSelector(state => state.competenciesReducer.competencies);

    const userRole = role || RoleType.none;
    const userIsCandidate = userRole === RoleType.student || userRole === RoleType.jobSeeker;
    const isValidCandidate = userIsCandidate && !!candidate && isAuthenticated;

    useEffect((): () => void => {
        if (skillOptions.length === 0) dispatch(fetchSkills());
        if (escoSkillOptions.length === 0) dispatch(fetchEscoSkills());
        if (competencyOptions.length === 0) dispatch(fetchCompetencies());

        return (): void => {
            dispatch(setActiveFilterValues({
                ...activeFilterValues,
                skills: [],
                competencies: [],
                skillsToLearn: [],
                competenciesToLearn: [],
            }));
        };
    }, []);

    useEffect((): void => {
        if (!isTalentMatching || !candidate) return;

        const combinedSkills = [...candidate.skills, ...candidate.escoSkills];
        const combinedSkillsToLearn = [...candidate.skillsToLearn, ...candidate.escoSkillsToLearn];

        dispatch(setActiveFilterValues({
            ...activeFilterValues,
            skills: combinedSkills.map(skill => skill.id),
            competencies: candidate.competencies.map(competency => competency.id),
            skillsToLearn: combinedSkillsToLearn.map(skill => skill.id),
            competenciesToLearn: candidate.competenciesToLearn.map(competency => competency.id),
        }));
    }, [dispatch, isTalentMatching, candidate]);

    const updateCandidate = (updatedCandidate: Partial<Candidate>): void => {
        if (candidate) {
            dispatch(patchCandidate({
                uuid: candidate.uuid,
                ...updatedCandidate,
            }));
        }
    };

    const updateFilters = (updatedFilters: Partial<InternshipVacancyOverviewFilterValues>): void => {
        dispatch(setActiveFilterValues({
            ...activeFilterValues,
            ...updatedFilters,
            pageNumber: 1,
        }));
    };

    const handleSubmitSkills = (skillsFormData: SkillsFormData): void => {
        const combinedSkills = [...skillsFormData.skills, ...skillsFormData.escoSkills];
        const skills = combinedSkills.map(skill => skill.id);

        updateFilters({ skills });
    };

    const handleSubmitSkillsToProfile = (skillsFormData: SkillsFormData): void => {
        updateCandidate({
            skills: skillsFormData.skills,
            escoSkills: skillsFormData.escoSkills,
        });
    };

    const handleSubmitCompetencies = (competenciesFormData: CompetenciesFormData): void => {
        const competencies = competenciesFormData.competencies.map(competency => competency.id);

        updateFilters({ competencies });
    };

    const handleSubmitCompetenciesToProfile = (competenciesFormData: CompetenciesFormData): void => {
        updateCandidate({
            competencies: competenciesFormData.competencies,
        });
    };

    const handleSubmitSkillsToLearn = (skillsFormData: SkillsFormData): void => {
        const combinedSkills = [...skillsFormData.skills, ...skillsFormData.escoSkills];
        const skillsToLearn = combinedSkills.map(skill => skill.id);

        updateFilters({ skillsToLearn });
    };

    const handleSubmitSkillsToLearnToProfile = (skillsFormData: SkillsFormData): void => {
        updateCandidate({
            skillsToLearn: skillsFormData.skills,
            escoSkillsToLearn: skillsFormData.escoSkills,
        });
    };

    const handleSubmitCompetenciesToLearn = (competenciesFormData: CompetenciesFormData): void => {
        const competenciesToLearn = competenciesFormData.competencies.map(competency => competency.id);

        updateFilters({ competenciesToLearn });
    };

    const handleSubmitCompetenciesToLearnToProfile = (competenciesFormData: CompetenciesFormData): void => {
        updateCandidate({
            competenciesToLearn: competenciesFormData.competencies,
        });
    };

    return (
        <InternshipTalentMatchingCard
            hasToLearnOptions
            isLoading={isLoading}
            isValidCandidate={isValidCandidate}
            activeFilterValues={activeFilterValues}
            skillOptions={[...skillOptions, ...escoSkillOptions]}
            skillsError={skillsError || escoSkillsError}
            competencyOptions={competencyOptions}
            onSubmitSkills={handleSubmitSkills}
            onSubmitSkillsToProfile={handleSubmitSkillsToProfile}
            onSubmitCompetencies={handleSubmitCompetencies}
            onSubmitCompetenciesToProfile={handleSubmitCompetenciesToProfile}
            onSubmitSkillsToLearn={handleSubmitSkillsToLearn}
            onSubmitSkillsToLearnToProfile={handleSubmitSkillsToLearnToProfile}
            onSubmitCompetenciesToLearn={handleSubmitCompetenciesToLearn}
            onSubmitCompetenciesToLearnToProfile={handleSubmitCompetenciesToLearnToProfile}
            className={className}
        />
    );
};

export default ConnectedInternshipTalentMatchingCard;
